import React, { useEffect, useState } from "react";
import { usePageMetadata } from "../context/PageMetadataContext";
import { Card, Row, Col, Table, Spinner } from "react-bootstrap";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, Cell
} from "recharts";
import { format, Locale, parseISO } from "date-fns";
import { enUS, vi, ko, zhCN } from 'date-fns/locale';
import { okx } from "../api/okx"; // Import to get Pi price
import { PiscanAPI } from "../api/piscan";
import { CardContent } from "../components/Card";
// import AdUnit from "../components/AdUnit";
import { useLanguage } from "../context/LanguageContext";
import AdSenseUnit from "../components/AdsenseUnit";
import AdUnit from "../components/AdUnit";

interface UnlockDataItem {
  claim_date: string;
  total_amount: number;
}

interface MainnetSupplyData {
  circulating_supply: number;
  migrated_mining_rewards: number;
  total_circulating_supply: number;
  total_locked: number;
  total_supply: number;
  updated_at: string;
}

const UnlockStatistics: React.FC = () => {
  const { t, language } = useLanguage();
  const [unlockData, setUnlockData] = useState<UnlockDataItem[]>([]);
  const [mainnetSupply, setMainnetSupply] = useState<MainnetSupplyData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { setHeading, setTitle, setDescription } = usePageMetadata();
  const [generatedAt, setGeneratedAt] = useState<string>("");
  const [highestDay, setHighestDay] = useState<{date: string, amount: number}>({date: "", amount: 0});
  const [piPrice, setPiPrice] = useState<number>(2.0); // Default value before loading

  // Get date-fns locale based on selected language
  const getDateLocale = () => {
    const locales: Record<string, Locale> = {
      'en': enUS,
      'vi': vi,
      'ko': ko,
      'zh': zhCN
    };
    return locales[language] || enUS;
  };

  useEffect(() => {
    setHeading(t('unlock_stats.heading'));
    setTitle(t('unlock_stats.title'));
    setDescription(t('unlock_stats.description'));
  }, [setHeading, setTitle, setDescription, t, language]);

  useEffect(() => {
    fetchData();
  }, []);

  // Get Pi price
  useEffect(() => {
    fetchPiPrice();
    // Refresh price every 30 seconds
    const interval = setInterval(fetchPiPrice, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchPiPrice = async () => {
    try {
      const marketData = await okx.getMarketData();
      setPiPrice(parseFloat(marketData.idxPx));
    } catch (err) {
      console.error("Failed to fetch Pi price:", err);
      // Keep using the default price if fetch fails
    }
  };

  useEffect(() => {
    if (unlockData.length > 0) {
      // Find the day with highest unlock amount
      const highest = unlockData.reduce((max, current) => 
        current.total_amount > max.total_amount ? current : max, unlockData[0]);
      setHighestDay({
        date: format(parseISO(highest.claim_date), 'MMM dd', { locale: getDateLocale() }),
        amount: highest.total_amount
      });
    }
  }, [unlockData, language]);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Fetch both datasets in parallel
      const [unlockResponse, supplyResponse] = await Promise.all([
        fetch(PiscanAPI.UnlockNext30Days).catch(() => ({ ok: false })),
        fetch(PiscanAPI.MainnetSupply).catch(() => ({ ok: false }))
      ]);
      
      // Handle unlock data
      let processedUnlockData = [];
      let generatedAtTimestamp = new Date().toISOString();
      
      if (unlockResponse.ok && 'json' in unlockResponse) {
        try {
          const unlockData = await unlockResponse.json();
          processedUnlockData = unlockData.data.map((item: { claim_date: string; total_amount: number; }) => ({
            ...item,
            formattedDate: format(parseISO(item.claim_date), 'MMM dd', { locale: getDateLocale() }),
            displayAmount: formatNumber(item.total_amount / 10_000_000, 2)
          }));
          generatedAtTimestamp = unlockData.generated_at;
        } catch (err) {
          console.error("Error processing unlock data:", err);
          // Keep default empty array
        }
      }
      
      // Handle supply data
      let supplyData = {
        circulating_supply: 7049498342.1165,
        migrated_mining_rewards: 7049498342.1165,
        total_circulating_supply: 7049498342.1165,
        total_locked: 5321994189.59379,
        total_supply: 10845382064.7946,
        updated_at: new Date().toISOString()
      };
      
      if (supplyResponse.ok && 'json' in supplyResponse) {
        try {
          supplyData = await supplyResponse.json();
        } catch (err) {
          console.error("Error processing supply data:", err);
          // Keep default data
        }
      }
      
      setUnlockData(processedUnlockData);
      setMainnetSupply(supplyData);
      setGeneratedAt(generatedAtTimestamp);
    } catch (err) {
      console.error(`Failed to fetch data: ${err instanceof Error ? err.message : String(err)}`);
      setError(t('common.loading_error'));
      // Set default values if everything fails
      setUnlockData([]);
      setMainnetSupply({
        circulating_supply: 7049498342.1165,
        migrated_mining_rewards: 7049498342.1165,
        total_circulating_supply: 7049498342.1165,
        total_locked: 5321994189.59379,
        total_supply: 10845382064.7946,
        updated_at: new Date().toISOString()
      });
      setGeneratedAt(new Date().toISOString());
    } finally {
      setLoading(false);
    }
  };

  // Calculate total and average unlock amount
  const totalUnlockAmount = unlockData.reduce((sum, item) => sum + item.total_amount, 0);
  const averageDaily = unlockData.length > 0 ? totalUnlockAmount / unlockData.length : 0;
  
  // Calculate percentage of total locked that will be unlocked
  const unlockPercentage = mainnetSupply && mainnetSupply.total_locked > 0 
    ? (totalUnlockAmount / (mainnetSupply.total_locked * 10_000_000)) * 100
    : 0;
  
  // Format number according to user locale
  const formatNumber = (value: number, decimalPlaces: number = 2): string => {
    return value.toLocaleString(language, {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimalPlaces
    });
  };

  // Format for display
  const formatPi = (value: number) => {
    return formatNumber(value / 10_000_000, 2);
  };

  // Format for mainnet supply (already in Pi units)
  const formatMainnetPi = (value: number) => {
    return formatNumber(value, 2);
  };

  // Format USD value
  const formatUSD = (piAmount: number) => {
    const usdValue = (piAmount / 10_000_000) * piPrice;
    return formatNumber(usdValue, 2);
  };

  // Format mainnet USD value
  const formatMainnetUSD = (piAmount: number) => {
    const usdValue = piAmount * piPrice;
    return formatNumber(usdValue, 2);
  };

  // Format Pi value with abbreviations (K, M, B)
  const formatPiAbbreviated = (value: number) => {
    const piValue = value / 10_000_000;
    
    if (piValue >= 1000000) {
      return `${formatNumber(piValue / 1000000, 1)}${t('number.million_short')}`;
    } else if (piValue >= 1000) {
      return `${formatNumber(piValue / 1000, 1)}${t('number.thousand_short')}`;
    } else {
      return formatNumber(piValue, 2);
    }
  };

  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const piValue = formatPi(payload[0].value);
      const piAbbreviated = formatPiAbbreviated(payload[0].value);
      const usdValue = formatUSD(payload[0].value);
      
      return (
        <div className="custom-tooltip bg-white p-2 shadow-sm border">
          <p className="mb-1 fw-bold">{label}</p>
          <p className="mb-0">
            <span className="text-primary">{piAbbreviated} π</span>
            <span className="ms-1 small text-muted">({piValue})</span>
          </p>
          <p className="mb-0 text-muted">
            ~${usdValue}
          </p>
        </div>
      );
    }
    return null;
  };

  // Generate gradient colors for bars
  const getBarColor = (value: number) => {
    const max = highestDay.amount;
    const intensity = value / max;
    // Generate a color from blue to purple based on intensity
    return `rgba(53, 162, 235, ${0.5 + intensity * 0.5})`;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">{t('common.loading')}</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return <div className="alert alert-danger">{error}</div>;
  }

  return (
    <div className="unlock-statistics-page">
      <div className="mb-4">
        <p className="text-muted">
          {t('unlock_stats.intro_text')}
          <small className="d-block mt-1">
            <i className="bi bi-info-circle me-1"></i>
            {t('unlock_stats.today_note')}
          </small>
          <small className="d-block mt-1">
            {t('unlock_stats.updated_at')} {generatedAt ? format(parseISO(generatedAt), 'MMM dd, yyyy HH:mm', { locale: getDateLocale() }) : t('unlock_stats.unknown')}
          </small>
        </p>
      </div>

      <Row className="mb-4">
        {/* Total Locked Amount */}
        <Col lg={3} md={6} className="mb-3">
          <Card className="h-100  shadow-sm">
            <Card.Body className="text-center">
              <h6 className="text-muted mb-2">{t('unlock_stats.total_locked')}</h6>
              <h3 className="fw-bold text-danger" style={{fontSize: "1.5rem"}}>
                {mainnetSupply ? formatMainnetPi(mainnetSupply.total_locked) : 0} π
              </h3>
              <p className="text-muted small">
                ~${mainnetSupply ? formatMainnetUSD(mainnetSupply.total_locked) : 0}
              </p>
            </Card.Body>
          </Card>
        </Col>
        
        {/* Total Projected Unlock */}
        <Col lg={3} md={6} className="mb-3">
          <Card className="h-100  shadow-sm">
            <Card.Body className="text-center">
              <h6 className="text-muted mb-2">{t('unlock_stats.total_next_30')}</h6>
              <h3 className="fw-bold text-primary" style={{fontSize: "1.5rem"}}>
                {formatPi(totalUnlockAmount)} π
              </h3>
              <p className="text-muted small">
                ~${formatUSD(totalUnlockAmount)} ({formatNumber(unlockPercentage, 2)}% {t('unlock_stats.of_total_locked')})
              </p>
            </Card.Body>
          </Card>
        </Col>
        
        {/* Average Daily Unlock */}
        <Col lg={3} md={6} className="mb-3">
          <Card className="h-100  shadow-sm">
            <Card.Body className="text-center">
              <h6 className="text-muted mb-2">{t('unlock_stats.avg_daily')}</h6>
              <h3 className="fw-bold text-info" style={{fontSize: "1.5rem"}}>
                {formatPi(averageDaily)} π
              </h3>
              <p className="text-muted small">
                ~${formatUSD(averageDaily)} {t('unlock_stats.per_day')}
              </p>
            </Card.Body>
          </Card>
        </Col>
        
        {/* Highest Unlock Day */}
        <Col lg={3} md={6} className="mb-3">
          <Card className="h-100  shadow-sm">
            <Card.Body className="text-center">
              <h6 className="text-muted mb-2">{t('unlock_stats.highest_day')}</h6>
              <h3 className="fw-bold text-warning" style={{fontSize: "1.5rem"}}>
                {formatPi(highestDay.amount)} π
              </h3>
              <p className="text-muted small">
                ~${formatUSD(highestDay.amount)} {t('unlock_stats.on')} {highestDay.date}
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <div className="row g-3 mb-4 d-block d-md-none">
        {/* <AdSenseUnit/> */}
        <AdUnit
                  className="67ccaa1c9302154e82f057e7"
                  width="300px"
                  height="100px"
                  centered={true}
                />
      </div>
      <Card className="mb-4  shadow-sm">
        <Card.Header className="bg-white border-bottom-0 pt-4 px-4">
          <h2 className="h5 mb-0 text-color-normal">{t('unlock_stats.chart_title')}</h2>
        </Card.Header>
        <Card.Body>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={unlockData}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 70
              }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis 
                dataKey="formattedDate" 
                angle={-45} 
                textAnchor="end"
                height={70}
                tick={{fontSize: 12}}
              />
              <YAxis 
                tickFormatter={(value) => formatPiAbbreviated(value)}
                label={{ 
                  value: t('unlock_stats.y_axis_label'), 
                  angle: -90, 
                  position: 'insideLeft',
                  style: { textAnchor: 'middle' }
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="total_amount" 
                name={t('unlock_stats.pi_unlock')}
                radius={[4, 4, 0, 0]}
              >
                {unlockData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getBarColor(entry.total_amount)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card.Body>
      </Card>
      
      <Card className=" shadow-sm">
        <Card.Header className="bg-white border-bottom-0 pt-4 px-4">
          <h2 className="h5 mb-0 text-color-normal">{t('unlock_stats.data_table')}</h2>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead>
                <tr>
                  <th>{t('unlock_stats.date')}</th>
                  <th className="text-end">{t('unlock_stats.amount')} (π)</th>
                  <th className="text-end">{t('unlock_stats.value')} (USD)</th>
                </tr>
              </thead>
              <tbody>
                {unlockData.map((item, index) => (
                  <tr key={index}>
                    <td>{format(parseISO(item.claim_date), 'MMM dd, yyyy', { locale: getDateLocale() })}</td>
                    <td className="text-end fw-bold">
                      {formatPi(item.total_amount)} π
                    </td>
                    <td className="text-end text-muted">
                      ${formatUSD(item.total_amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default UnlockStatistics;